// ============================================
// 编辑器选中态持久化（2026-09-14）
// 背景：刷新浏览器后属性面板回到"未选中"空态，用户需重新点选模型才能看到属性详情。
// 方案：按场景把上次选中的模型 id 存 localStorage，场景模型就绪后自动恢复一次；
//       恢复前先校验模型仍存在（避免已删除/换场景的脏 id）。
// 说明：恢复经 selectionStore + sceneStore 双写——属性面板立即可用（不依赖 3D 画布懒加载完成）。
// ============================================

import { useEffect, useRef } from 'react';
import useSceneStore from '../stores/sceneStore';
import useSelectionStore from '../stores/selectionStore';

const KEY_PREFIX = 'dtwin-editor-selection:';

export function selectionStorageKey(sceneId: string): string {
  return `${KEY_PREFIX}${sceneId}`;
}

export function readSavedSelection(sceneId: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(selectionStorageKey(sceneId));
  } catch {
    return null;
  }
}

export function writeSavedSelection(sceneId: string, modelId: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (modelId) localStorage.setItem(selectionStorageKey(sceneId), modelId);
    else localStorage.removeItem(selectionStorageKey(sceneId));
  } catch {
    /* 忽略存储失败 */
  }
}

/**
 * 在场景编辑器内调用：模型列表就绪后恢复上次选中；之后选中变化写回。
 * @param sceneId 场景 id（切换场景时按新场景重新恢复）
 */
export function useSelectionPersistence(sceneId: string | undefined): void {
  const selectedModelId = useSceneStore((s) => s.selectedModelId);
  const modelCount = useSceneStore((s) => s.models.length);
  const restoredRef = useRef(false);

  // 切换场景：允许对新场景重新恢复
  useEffect(() => {
    restoredRef.current = false;
  }, [sceneId]);

  // 恢复（每个场景仅一次；等待模型列表就绪）
  useEffect(() => {
    if (restoredRef.current || !sceneId || modelCount === 0) return;
    restoredRef.current = true;
    const saved = readSavedSelection(sceneId);
    if (!saved) return;
    const models = useSceneStore.getState().models;
    if (!models.some((m) => m.id === saved)) return;
    // 双写：属性面板（sceneStore）与画布高亮/gizmo（selectionStore → SelectionBridge 同步）
    useSelectionStore.getState().select(saved);
    useSceneStore.getState().selectModel(saved);
  }, [sceneId, modelCount]);

  // 写回（恢复完成后才写，避免初始 null 覆盖已保存值；用 getState 取最新值，避免与恢复同批次的旧闭包值）
  useEffect(() => {
    if (!sceneId || !restoredRef.current) return;
    writeSavedSelection(sceneId, useSceneStore.getState().selectedModelId);
  }, [sceneId, selectedModelId]);
}
